import {
  type AvailableReviewersResponse,
  AvailableReviewersResponseSchema,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/available_reviewers'
import {create} from '@github-ui/dotcom-schema/protobuf'
import {keepPreviousData, useQuery, type QueryKey} from '@github-ui/react-query'
import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {legacyReviewerToProto} from '../payloads/review-requests'
import type {LegacyAvailableReviewersData} from '../payloads/available-reviewers'

function availableReviewersQueryKey(apiUrl: string): QueryKey {
  return [PageData.availableReviewers, apiUrl]
}

// Temporary legacy -> proto shim for the reviewers (available-reviewers)
// endpoint. With the `available_reviewers_proto` flag off the backend still
// returns the legacy JSON; this converts it up to the canonical proto shape so
// the picker only ever observes proto `Reviewer`s. Deletes with the flag.
/** Converts the legacy (flag-off) available_reviewers JSON up to the proto shape. */
export function normalizeAvailableReviewersResponse(data: LegacyAvailableReviewersData): AvailableReviewersResponse {
  return create(AvailableReviewersResponseSchema, {
    availableReviewers: data.availableReviewers.map(legacyReviewerToProto),
  })
}

/**
 * Loads the reviewer typeahead suggestions from
 * `GET /pull/:id/page_data/reviewers?q=<query>`. When `query` is empty, the
 * server still returns a capped initial set of users.
 *
 * The backend serves proto3-JSON (flagged by the `X-Proto-JSON` header) when
 * `available_reviewers_proto` is on, which `queryFnFetch` decodes through
 * `AvailableReviewersResponseSchema`; otherwise the legacy JSON is converted up
 * to the same proto shape. Previous results are kept while a new request is in
 * flight so the picker doesn't flash to an empty state as the user types.
 */
export function useAvailableReviewers({query, enabled = true}: {query?: string; enabled?: boolean} = {}) {
  const params = query ? `q=${encodeURIComponent(query)}` : undefined
  const apiUrl = usePageDataUrl(PageData.availableReviewers, params)
  return useQuery<AvailableReviewersResponse>({
    queryKey: availableReviewersQueryKey(apiUrl),
    queryFn: async () => {
      const response = await queryFnFetch<typeof AvailableReviewersResponseSchema, LegacyAvailableReviewersData>(
        {queryDeps: {pathname: apiUrl}, routeId: 'pull-requests', queryName: 'availableReviewers'},
        {schema: AvailableReviewersResponseSchema},
      )
      // Proto-decoded responses carry `$typeName`; legacy JSON does not.
      return '$typeName' in response ? response : normalizeAvailableReviewersResponse(response)
    },
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  })
}
